import { AnimatePresence } from 'motion/react'
import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import type { Person } from '../../shared/events'
import type { Pack } from '../../shared/pack/schema'
import { LearnDone } from '../arcade/LearnDone'
import { usePrefetchPlaceAudio } from '../audio/placePrefetch'
import { useMachineId } from '../arcade/machines'
import { hasLanguage } from '../domain/arcade/session'
import { rightOn } from '../domain/arcade/plays'
import { rewardMachineFor } from '../domain/rewardMachine'
import { needsYou } from '../domain/rewards'
import { MyPrizesSheet } from '../arcade/MyPrizesSheet'
import { PlaysSheet } from '../arcade/PlaysSheet'
import { useDailyGoalReward } from '../arcade/useEarnings'
import { Reward } from '../arcade/Reward'
import { Session } from '../arcade/Session'
import { useArcade } from '../arcade/useArcade'
import { loadClips, setSpeechLocale } from '../audio/tts'
import { usePack } from '../data/pack'
import { useTripPack } from '../data/trip'
import { Unwrap } from '../screens/Unwrap'
import { InstallPage, PrivacyPage } from '../screens/InfoPages'
import { PastTripSheet, PastTripsSheet } from '../screens/PastTripsSheet'
import { TripBookScreen } from '../screens/TripBook'
import { useCrateNew, useUnwrap, useVisibleTrip } from '../data/controls'
import { useSession } from '../data/session'
import { playsOf } from '../domain/players'
import { useSettings } from '../data/settings'
import { startSync } from '../data/sync'
import { useBatteryPrefersFlat, useMotionTier, useTierMonitors } from '../motion/tier'
import { ArcadeScreen } from '../screens/ArcadeScreen'
import { CrateScreen } from '../screens/CrateScreen'
import { MapScreen } from '../screens/MapScreen'
import { IdeasSheet, RulesSheet, SettingsSheet, StageSheet } from '../screens/Sheets'
import { TripScreen } from '../screens/TripScreen'
import { LearnScreen } from '../screens/LearnScreen'
import { MyPlanSheet } from '../screens/MyPlanSheet'
import { PlaceSheet } from '../screens/PlaceSheet'
import type { SetId } from '../stage/types'
import { SwitchSheet } from '../screens/SwitchSheet'
import { WantSheet } from '../screens/WantSheet'
import { WhoScreen } from '../screens/WhoScreen'
import { setTripPlaces, setTripSeasons, useStage } from '../stage/bridge'
import { islandNow, mapView } from '../map/bindings'
import { learnBadge, todayNext } from '../map/journey'
import type { TripClock } from '../domain/tripClock'
import { placeId, tripPlaces } from '../map/places/registry'
import { tripSeasons, type TripSeason } from '../map/seasons'
import { t } from '../strings/en'
import { Dock } from './Dock'
import { ParentModeClock } from './ParentModeClock'
import { goTab, GROWN_UP_TABS, showJoin, TABS, takeLanding, useRoute } from './router'
import { useToast } from './toast'
import { worldHoldsUnder } from './worldHold'
import { useRulesChangedNote } from './useRulesNote'
import { useTripNow } from './useTripNow'
import { useWorld } from './useWorld'
import { OrganiserLock, useOrganiserLock } from './OrganiserLock'
import { syncOwnedPacks } from '../data/packFiles'
import { usePacks } from '../data/packs'
import { Moved, useMoved } from '../screens/Moved'

// The island the Map opens on loads with the stage (its own chunk), so it shows at once.
const WorldStage = lazy(() =>
  Promise.all([import('../stage/WorldStage'), import('../stage/mapIslands').then((m) => m.preloadIsland(useStage.getState().island))]).then(([m]) => m),
)
// Grown-up screens come in their own chunk (B17); children never load them.
const grownUp = () => import('../organiser/lazy')
const PlanScreen = lazy(() => grownUp().then((m) => ({ default: m.PlanScreen })))
const BonusSheet = lazy(() => grownUp().then((m) => ({ default: m.BonusSheet })))
const ControlsSheet = lazy(() => grownUp().then((m) => ({ default: m.ControlsSheet })))
const FamilySheet = lazy(() => grownUp().then((m) => ({ default: m.FamilySheet })))
const OrganiserSheet = lazy(() => grownUp().then((m) => ({ default: m.OrganiserSheet })))
const OrganiserScreen = lazy(() => grownUp().then((m) => ({ default: m.OrganiserScreen })))
const RecoverySheet = lazy(() => grownUp().then((m) => ({ default: m.RecoverySheet })))
const AdminChangeSheet = lazy(() => grownUp().then((m) => ({ default: m.AdminChangeSheet })))
const SeeAsSheet = lazy(() => grownUp().then((m) => ({ default: m.SeeAsSheet })))
const PrizesSheet = lazy(() => grownUp().then((m) => ({ default: m.PrizesSheet })))
const RewardsSheet = lazy(() => grownUp().then((m) => ({ default: m.RewardsSheet })))
const PersonSheet = lazy(() => grownUp().then((m) => ({ default: m.PersonSheet })))
const PhonesSheet = lazy(() => grownUp().then((m) => ({ default: m.PhonesSheet })))
const PinSheet = lazy(() => grownUp().then((m) => ({ default: m.PinSheet })))
const PinGate = lazy(() => grownUp().then((m) => ({ default: m.PinGate })))
const TripsSheet = lazy(() => grownUp().then((m) => ({ default: m.TripsSheet })))
const UnlockSheet = lazy(() => grownUp().then((m) => ({ default: m.UnlockSheet })))
const AtlasSheet = lazy(() => grownUp().then((m) => ({ default: m.AtlasSheet })))
const NewTripSheet = lazy(() => grownUp().then((m) => ({ default: m.NewTripSheet })))
const CopyTripSheet = lazy(() => grownUp().then((m) => ({ default: m.CopyTripSheet })))
const DeleteFamilySheet = lazy(() => grownUp().then((m) => ({ default: m.DeleteFamilySheet })))

/** Pack colours become CSS variables, so a new trip can re-theme without a rebuild. */
function useTheme(pack: Pack | null) {
  useEffect(() => {
    if (!pack) return
    for (const [k, v] of Object.entries(pack.theme.colors)) document.documentElement.style.setProperty(`--${k}`, v)
    // Speech in the trip's language (D3); a trip with no language kit keeps the phone's own.
    setSpeechLocale(pack.meta.locale ?? navigator.language ?? 'en-US')
    // Recorded card audio, when the pack has some (S4).
    void loadClips(pack.meta.id)
    // The trip's reward machine (B16): the parent's choice, the kit's, or the neutral one.
    useMachineId.setState({ id: rewardMachineFor(pack) })
  }, [pack])
}

/**
 * The trip's places (the places its stages stand on, as the Map shows them) go to the world stage:
 * they follow the trip's live weather, come first in the island switcher and are remembered. A
 * phone with no island remembered opens on the stage the family is in now (or next).
 */
function useTripPlaces(pack: Pack, clock: TripClock) {
  const view = mapView(pack)
  const ids = tripPlaces(view.itinerary.stages)
  const key = ids.join(',')
  const start = islandNow(view, clock, ids)
  useEffect(() => setTripPlaces(key ? key.split(',') : [], start), [key, start])
  // Each trip place's season follows its stage's dates (map/seasons); World → Season overrides it.
  const seasons = JSON.stringify(tripSeasons(view.itinerary.stages, clock.today, placeId))
  useEffect(() => setTripSeasons(JSON.parse(seasons) as Record<string, TripSeason>), [seasons])
  // Their sounds and voice clips, cached for offline once the first load has settled (WI-24).
  usePrefetchPlaceAudio(key)
}

export function App() {
  const seed = usePack((s) => s.pack)
  // The trip as edited in the app (trip.* events on top of the pack's seed).
  const pack = useTripPack(seed)
  const packError = usePack((s) => s.error)
  const route = useRoute()
  const { person, ready, linkState } = useSession()
  const glove = useSettings((s) => s.glove)
  const [booted, setBooted] = useState(false)
  // A2: on the old address once the app has moved, every phone sees where to (screens/Moved).
  const moveTo = useMoved()
  // An unlinked phone (link state 'needs-link', WI-22) shows /join; once linked it goes on to the
  // route first asked for. While the state is still 'unknown' the URL is left alone.
  useEffect(() => {
    if (linkState === 'needs-link') showJoin(true)
    else if (linkState === 'linked') showJoin(false)
  }, [linkState, route.tab])

  useEffect(() => {
    void Promise.all([useSession.getState().restore(), useSettings.getState().restore()]).then(() => setBooted(true))
    void usePack.getState().load()
  }, [])
  useEffect(() => {
    document.documentElement.dataset.glove = glove ? '1' : '0'
  }, [glove])
  useEffect(() => {
    if (!person) return
    return startSync({ renew: () => useSession.getState().renew(), personId: () => useSession.getState().person?.id })
  }, [person])
  useTheme(pack)
  useTierMonitors()

  // The install and privacy pages open for anyone, signed in or not (B14, B12).
  if (route.tab === 'install') return <InstallPage />
  if (route.tab === 'privacy') return <PrivacyPage />
  if (!booted || !ready) return null
  // Making a recovery code first (a parent's phone) opens its sheet over the app; back, the note again.
  if (moveTo && !(route.overlay === 'recovery' && person?.kind === 'admin')) return <Moved to={moveTo} />
  if (!person) return <WhoScreen pack={pack} />
  if (!pack) return <main className="screen center">{packError ? t.packError : t.loading}</main>
  return <Shell pack={pack} />
}

function Shell({ pack }: { pack: Pack }) {
  const person = useSession((s) => s.person)
  if (!person) return null
  // The admin PIN is mandatory (WI-16): a parent from before that sets one before anything else.
  if (person.kind === 'admin' && !person.pin) {
    return (
      <Suspense fallback={null}>
        <PinGate />
      </Suspense>
    )
  }
  return <ShellFor pack={pack} person={person} />
}

function ShellFor({ pack: trip, person }: { pack: Pack; person: Person }) {
  // Children see the trip through their visibility settings (surprises, how far ahead).
  const pack = useVisibleTrip(trip, person)
  const route = useRoute()
  // B3: the grown-up screens ask for the PIN again after 10 minutes away (domain/organiserLock).
  const locked = useOrganiserLock(route.tab, route.overlay)
  const arcade = useArcade(pack, person.id)
  // Bonus plays are for someone else who plays (WI-37: a grown-up who plays can't give themselves any).
  const bonusFor = useSession((s) => s.people?.find((p) => playsOf(p) && p.id !== person.id)?.id)
  useDailyGoalReward(person, arcade)
  // Grown-ups get the organiser chunk straight away, so their first sheet opens without a wait.
  useEffect(() => {
    if (person.kind !== 'child') void grownUp()
  }, [person.kind])
  // C5: the family's packs; a change reloads the trip, and an owned pack is kept for offline.
  useEffect(() => {
    void syncOwnedPacks().then((bought) => {
      if (!bought) return
      // Back from Stripe Checkout with a new pack (C3).
      const { catalogue, owned } = usePacks.getState()
      useToast.getState().show(t.packs.unwrapped(catalogue.find((c) => owned?.includes(c.id))?.name ?? ''), 3500)
    })
  }, [person.id])
  // A parent who has just started a family lands on Organiser home and its checklist (WI-19).
  useEffect(() => {
    const tab = takeLanding()
    if (tab && person.kind !== 'child') goTab(tab)
  }, [person.kind])
  useRulesChangedNote(person)
  const now = useTripNow(pack)
  useTripPlaces(pack, now.clock)
  // No language kit (B6): no Learn tab; the Arcade plays Explorer.
  const language = hasLanguage(pack)
  // The dock's cues (owner, 23 Oct: they replace the Map's action card): today's Learn goal (none
  // for someone who doesn't play), and a dot on Trip while there's a plan today.
  const goal = language && playsOf(person) ? arcade.settings?.learn.goal : undefined
  const learnLeft = learnBadge(goal === undefined ? null : Math.max(0, goal - rightOn(arcade.events, person.id, arcade.day)))
  const planToday = !!todayNext(now.clock, now.nowMs, now.agenda)
  // The Crate's badge: places found since this person last opened it here (not the trip's ideas).
  const crateNew = useCrateNew(pack, person, route.tab === 'crate')
  // The Organiser tab's badge (WI-19): what's waiting for a grown-up (prizes, activities, wishes).
  const people = useSession((s) => s.people)
  const ownedPacks = usePacks((s) => s.owned)
  const waiting = useMemo(
    () => (person.kind !== 'child' && people ? needsYou(trip, people, arcade.events, now.clock.today, person.id, ownedPacks).length : 0),
    [person.kind, person.id, people, trip, arcade.events, now.clock.today, ownedPacks],
  )
  // A stage that was wrapped for this child has opened: unwrap it once (A3).
  const unwrap = useUnwrap(trip, person)
  const toast = useToast((s) => s.text)
  const toastAction = useToast((s) => s.action)
  const world = useWorld(pack, now)
  const tier = useMotionTier()
  const resting = useBatteryPrefersFlat()
  const stageFailed = useStage((s) => s.status) === 'failed'

  // One world stage behind every tab. Off the Map, Calm/Still show the flat screens instead
  // (docs/REMEDIATION_3D_LOOK.md); the Map keeps its frozen diorama in Calm/Still.
  const stageOn = !resting && !stageFailed
  // The itinerary editor is plain organiser chrome: the world rests (hidden, paused) behind it.
  const planning = route.tab === 'plan' && person.kind !== 'child'
  // WI-34: every tab keeps its 3D set in Calm and Still too, as a still frame (each set's setTier).
  const stageShown = stageOn && !planning
  // Organiser home (WI-19) stands on the Trip set: the toy world of the trip it organises.
  const stageTab: SetId =
    route.tab === 'plan' || route.tab === 'organiser' ? 'trip' : route.tab === 'install' || route.tab === 'privacy' || route.tab === 'join' ? 'map' : route.tab
  const grown = person.kind !== 'child'
  useEffect(() => {
    document.documentElement.dataset.stage = stageShown ? 'on' : 'off'
  }, [stageShown])
  useEffect(() => {
    document.documentElement.dataset.tier = tier
  }, [tier])

  const overlay = route.overlay
  const stageId = overlay?.startsWith('stage:') ? overlay.slice(6) : null
  const fullScreen = overlay?.startsWith('session:') || overlay === 'reward' || overlay === 'learn-done' || overlay?.startsWith('trip-book:')
  // A modal sheet covers the world and makes the rest inert: the world holds its last frame
  // (stage 3 root cause: a world drawing every frame behind Settings › Switch kept the main
  // thread so busy on a slow phone, or software GL, that taps on the sheet took seconds to land).
  // The World panel isn't modal: the world stays live behind it, so time and weather show. A stop
  // or place card stays live too: the camera moves to what it's about (Trip's stage focus, a pin).
  const underSheet = worldHoldsUnder(overlay)
  // `my-plan:<date>` adds a child's own plan; `my-plan:<date>:<itemId>` edits one.
  const myPlanKey = overlay?.startsWith('my-plan:') ? overlay.slice(8).split(':') : null
  // `admin:<co|transfer|revoke>:<personId>` (B15).
  const adminKey = overlay?.match(/^admin:(co|transfer|revoke):(.+)$/)
  const adminChange = adminKey ? { mode: adminKey[1] as 'co' | 'transfer' | 'revoke', personId: adminKey[2]! } : null
  const myPlan = myPlanKey?.[0] ? { date: myPlanKey[0], item: pack.itinerary.dayItems.find((i) => i.id === myPlanKey[1]) ?? null } : null
  if (locked) return <OrganiserLock />

  return (
    <>
      {stageOn ? (
        <Suspense fallback={null}>
          <WorldStage tab={stageTab} world={world} tier={tier} shown={stageShown} paused={!!fullScreen || planning || underSheet} />
        </Suspense>
      ) : null}
      {/* /join on a linked phone shows the Map until the router moves on to it. */}
      {(route.tab === 'map' || route.tab === 'join') && <MapScreen pack={pack} person={person} now={now} world={world} flat={!stageOn} />}
      {route.tab === 'trip' && <TripScreen pack={pack} now={now} person={person} />}
      {route.tab === 'learn' && (language ? <LearnScreen pack={pack} person={person} /> : <MapScreen pack={pack} person={person} now={now} world={world} flat={!stageOn} />)}
      {route.tab === 'arcade' && <ArcadeScreen pack={pack} person={person} />}
      {route.tab === 'crate' && <CrateScreen pack={pack} person={person} />}
      {planning && (
        <Suspense fallback={null}>
          <PlanScreen pack={trip} person={person} />
        </Suspense>
      )}
      {route.tab === 'plan' && !planning && <MapScreen pack={pack} person={person} now={now} world={world} flat={!stageOn} />}
      {route.tab === 'organiser' &&
        (grown ? (
          <Suspense fallback={null}>
            <OrganiserScreen pack={trip} person={person} onStage={stageShown} />
          </Suspense>
        ) : (
          <MapScreen pack={pack} person={person} now={now} world={world} flat={!stageOn} />
        ))}
      {fullScreen || planning ? null : (
        <Dock
          active={route.tab}
          badges={{
            arcade: arcade.totalLeft ? { n: arcade.totalLeft, label: t.dockBadge.plays(arcade.totalLeft) } : null,
            learn: learnLeft ? { n: learnLeft, soft: true, label: t.dockBadge.learnLeft(learnLeft) } : null,
            trip: planToday ? { dot: true, label: t.dockBadge.today } : null,
            crate: crateNew ? { n: crateNew, label: t.dockBadge.crateNew(crateNew) } : null,
            organiser: waiting ? { n: waiting, label: t.dockBadge.needsYou(waiting) } : null,
          }}
          tabs={grown ? GROWN_UP_TABS : TABS}
          hidden={language ? [] : ['learn']}
        />
      )}
      {unwrap.stage && !fullScreen ? <Unwrap stage={unwrap.stage} onDone={unwrap.done} /> : null}
      <AnimatePresence>
        {overlay === 'settings' && <SettingsSheet key="settings" pack={pack} person={person} />}
        {/* Settings › Switch (WI-17): a sheet like the others, over Settings (WI-18). */}
        {overlay === 'who' && <SwitchSheet key="who" />}
        {overlay === 'ideas' && <IdeasSheet key="ideas" pack={pack} person={person} />}
        {overlay === 'want' && <WantSheet key="want" pack={pack} person={person} />}
        {overlay?.startsWith('place:') && <PlaceSheet key={overlay} pack={pack} person={person} placeId={overlay.slice(6)} />}
        {overlay === 'rules' && person.kind === 'child' && <RulesSheet key="rules" person={person} />}
        {myPlan && <MyPlanSheet key={overlay} pack={pack} person={person} date={myPlan.date} item={myPlan.item} />}
        {stageId && <StageSheet key={overlay} pack={pack} stageId={stageId} />}
        {overlay === 'pin' && person.kind !== 'child' && (
          <Suspense key="pin" fallback={null}>
            <PinSheet />
          </Suspense>
        )}
        {overlay === 'phones' && person.kind === 'admin' && (
          <Suspense key="phones" fallback={null}>
            <PhonesSheet />
          </Suspense>
        )}
        {overlay === 'family' && person.kind === 'admin' && (
          <Suspense key="family" fallback={null}>
            <FamilySheet />
          </Suspense>
        )}
        {overlay?.startsWith('person:') && person.kind === 'admin' && (
          <Suspense key={overlay} fallback={null}>
            <PersonSheet target={overlay.slice(7)} />
          </Suspense>
        )}
        {overlay?.startsWith('controls:') && person.kind === 'admin' && (
          <Suspense key={overlay} fallback={null}>
            <ControlsSheet childId={overlay.slice(9)} pack={trip} />
          </Suspense>
        )}
        {overlay === 'organiser' && person.kind !== 'child' && (
          <Suspense key="organiser" fallback={null}>
            <OrganiserSheet pack={trip} person={person} />
          </Suspense>
        )}
        {overlay?.startsWith('see-as:') && person.kind !== 'child' && (
          <Suspense key={overlay} fallback={null}>
            <SeeAsSheet pack={trip} childId={overlay.slice(7)} />
          </Suspense>
        )}
        {overlay === 'recovery' && person.kind === 'admin' && (
          <Suspense key="recovery" fallback={null}>
            <RecoverySheet />
          </Suspense>
        )}
        {adminChange && person.kind === 'admin' && (
          <Suspense key={overlay} fallback={null}>
            <AdminChangeSheet mode={adminChange.mode} personId={adminChange.personId} />
          </Suspense>
        )}
        {/* C6, C7: unwrapping a pack, and the Atlas (grown-ups; a child asks from the Map). */}
        {overlay?.startsWith('unlock:') && person.kind !== 'child' && (
          <Suspense key={overlay} fallback={null}>
            <UnlockSheet packId={overlay.slice(7)} />
          </Suspense>
        )}
        {overlay === 'atlas' && person.kind !== 'child' && (
          <Suspense key="atlas" fallback={null}>
            <AtlasSheet />
          </Suspense>
        )}
        {overlay === 'trips' && person.kind !== 'child' && (
          <Suspense key="trips" fallback={null}>
            <TripsSheet person={person} />
          </Suspense>
        )}
        {overlay === 'family-delete' && person.kind === 'admin' && (
          <Suspense key="family-delete" fallback={null}>
            <DeleteFamilySheet />
          </Suspense>
        )}
        {overlay?.startsWith('copy-trip:') && person.kind === 'admin' && (
          <Suspense key={overlay} fallback={null}>
            <CopyTripSheet tripId={overlay.slice(10)} />
          </Suspense>
        )}
        {overlay === 'new-trip' && person.kind === 'admin' && (
          <Suspense key="new-trip" fallback={null}>
            <NewTripSheet />
          </Suspense>
        )}
        {overlay === 'prizes' && person.kind === 'admin' && (
          <Suspense key="prizes" fallback={null}>
            <PrizesSheet person={person} />
          </Suspense>
        )}
        {overlay === 'rewards' && person.kind === 'admin' && (
          <Suspense key="rewards" fallback={null}>
            <RewardsSheet person={person} />
          </Suspense>
        )}
        {overlay === 'plays' && <PlaysSheet key="plays" pack={pack} person={person} />}
        {overlay === 'my-prizes' && playsOf(person) && <MyPrizesSheet key="my-prizes" person={person} />}
        {overlay === 'past-trips' && <PastTripsSheet key="past-trips" person={person} />}
        {overlay?.startsWith('past-trip:') && <PastTripSheet key={overlay} person={person} tripId={overlay.slice(10)} />}
        {overlay?.startsWith('trip-book:') && <TripBookScreen key={overlay} person={person} tripId={overlay.slice(10)} />}
        {overlay === 'bonus' && bonusFor && person.kind !== 'child' && (
          <Suspense key="bonus" fallback={null}>
            <BonusSheet pack={pack} playerId={bonusFor} organiserId={person.id} />
          </Suspense>
        )}
      </AnimatePresence>
      {overlay?.startsWith('session:') ? <Session pack={pack} personId={person.id} sessionKey={overlay.slice('session:'.length)} /> : null}
      {overlay === 'reward' ? <Reward pack={pack} personId={person.id} /> : null}
      {overlay === 'learn-done' ? <LearnDone pack={pack} personId={person.id} /> : null}
      {/* Parent mode (WI-17): the clock pauses while a sheet or the plan is open; the Map's days
          pill shows it. */}
      <ParentModeClock paused={!!overlay || route.tab === 'plan'} />
      {toast ? (
        <div className={`toast${toastAction ? ' has-action' : ''}`} role="status">
          {toast}
          {toastAction ? (
            <button
              className="toast-action press"
              onClick={() => {
                toastAction.run()
                useToast.getState().hide()
              }}
            >
              {toastAction.label}
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  )
}
